import { PageHero, SectionHeader } from "@/components/Section";
import useReveal from "@/hooks/useReveal";
import {
  Heart,
  Smartphone,
  DollarSign,
  Mail,
  Repeat,
  ShieldCheck,
  ExternalLink,
  ArrowRight,
  Sparkles,
  Users,
  Globe,
  Church,
  Check,
} from "lucide-react";
import { church } from "@/config/church";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const quickAmounts = [25, 50, 100, 250, 500];

const funds = [
  { id: "tithe", label: "Tithe & Offering", desc: "General fund — supports every ministry of the church.", icon: Church },
  { id: "missions", label: "Missions", desc: "Local outreach and global gospel work.", icon: Globe },
  { id: "building", label: "Building Fund", desc: "Facility upkeep and expansion for future generations.", icon: Sparkles },
  { id: "benevolence", label: "Benevolence", desc: "Direct help for families in crisis.", icon: Heart },
  { id: "youth", label: "Youth Ministry", desc: "Discipling the next generation.", icon: Users },
];

const frequencies = ["One-time", "Weekly", "Bi-weekly", "Monthly"];

const impacts = [
  { icon: Church,  n: "3",  label: "Weekly gatherings powered by your gifts" },
  { icon: Users,   n: "14", label: "Ministries serving every generation" },
  { icon: Globe,   n: "∞",  label: "Souls reached with the gospel — this is why we give" },
];

function cashappUrl(amount) {
  const tag = church.giving.cashapp.replace(/^\$/, "");
  return amount && Number(amount) > 0
    ? `https://cash.app/$${tag}/${amount}`
    : church.giving.cashappUrl;
}

export default function Give() {
  useReveal();
  const navigate = useNavigate();
  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [fund, setFund] = useState(funds[0].label);
  const [frequency, setFrequency] = useState(frequencies[0]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const effectiveAmount = Number(customAmount || amount || 0);

  const submitIntent = async (e) => {
    e.preventDefault();
    if (!form.name || !effectiveAmount) {
      toast.error("Please add your name and a gift amount.");
      return;
    }
    try {
      setBusy(true);
      await api.post("/donation-intent", {
        ...form,
        amount: effectiveAmount,
        fund,
        frequency,
        method: "Cash App",
      });
      setDone(true);
      // Open Cash App in a new tab with amount pre-filled, then navigate to thank-you
      window.open(cashappUrl(effectiveAmount), "_blank", "noopener");
      navigate("/thank-you?source=donation");
    } catch {
      toast.error("Could not record your gift. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="give-page">
      <PageHero
        eyebrow="Give With Joy"
        title="Generosity is a lifestyle."
        subtitle="Every gift helps us preach the gospel, disciple believers, and love our neighbors well. Thank you for stewarding what God has entrusted to you."
        image="https://images.pexels.com/photos/9577018/pexels-photo-9577018.jpeg?auto=compress&cs=tinysrgb&w=2000"
      >
        <div className="flex flex-wrap items-center gap-3">
          <a href="#give-now" className="btn-primary">
            <Heart className="h-4 w-4" /> Give Now
          </a>
          <a href="#ways" className="btn-outline">Ways to Give</a>
        </div>
      </PageHero>

      {/* GIVE NOW — the main donation panel */}
      <section id="give-now" className="bg-white py-24">
        <div className="container-npbc grid gap-14 lg:grid-cols-[1.05fr_1fr] items-start">
          {/* Left: Why + verse */}
          <div data-reveal>
            <div className="eyebrow">Why We Give</div>
            <h2 className="mt-3 heading-serif text-5xl leading-tight text-balance">
              “Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver.”
            </h2>
            <div className="mt-4 text-npbc-red font-semibold tracking-wide">— 2 Corinthians 9:7</div>
            <p className="mt-6 text-npbc-gray leading-relaxed">
              Giving at New Philadelphia is an act of worship. Whether you tithe, sow a
              seed of faith, or give to missions, every dollar is stewarded with integrity,
              prayer, and Kingdom purpose.
            </p>
            <div className="mt-8 flex items-center gap-3 text-sm text-npbc-gray/80">
              <ShieldCheck className="h-4 w-4 text-npbc-red" />
              Secure · Prayerful · Accountable
            </div>

            {/* Impact strip */}
            <div className="mt-12 grid grid-cols-3 gap-4">
              {impacts.map((i) => (
                <div key={i.label} className="rounded-2xl border border-black/[0.06] bg-npbc-cream p-5">
                  <div className="h-9 w-9 rounded-xl bg-npbc-red/10 text-npbc-red grid place-items-center">
                    <i.icon className="h-4 w-4" />
                  </div>
                  <div className="mt-4 heading-serif text-3xl leading-none">{i.n}</div>
                  <div className="mt-2 text-xs text-npbc-gray/80 leading-relaxed">{i.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Donation panel */}
          <div data-reveal className="relative rounded-3xl overflow-hidden p-8 md:p-10 text-white bg-npbc-black">
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-npbc-gold/15 blur-3xl" />
            <div className="absolute -left-16 -bottom-16 h-72 w-72 rounded-full bg-npbc-red/15 blur-3xl" />
            <div className="relative">
              <div className="eyebrow !text-npbc-gold">Give Now</div>
              <h3 className="mt-3 heading-serif text-4xl leading-tight">
                Send your gift in seconds.
              </h3>
              <p className="mt-2 text-white/70 text-sm">
                Pick an amount, choose a fund, and complete your gift on Cash App —
                <span className="text-npbc-gold font-semibold"> {church.giving.cashapp}</span>.
              </p>

              {done ? (
                <div className="mt-8 rounded-2xl border border-white/15 bg-white/[0.05] p-6 text-center">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-npbc-gold text-npbc-black">
                    <Check className="h-6 w-6" />
                  </div>
                  <div className="mt-4 heading-serif text-2xl">Thank you, {form.name.split(" ")[0]}!</div>
                  <p className="mt-2 text-white/75 text-sm">
                    Cash App is opening in a new tab pre-filled with <b>${effectiveAmount}</b> for <b>{fund}</b>.
                    If it didn't open, tap below.
                  </p>
                  <a
                    href={cashappUrl(effectiveAmount)}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="btn-gold mt-5 w-full justify-center"
                  >
                    Open Cash App {church.giving.cashapp}
                    <ExternalLink className="h-4 w-4 opacity-70" />
                  </a>
                  <button
                    type="button"
                    onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", notes: "" }); setCustomAmount(""); setAmount(100); }}
                    className="mt-3 text-xs uppercase tracking-[0.24em] text-white/60 hover:text-white"
                  >
                    Give again
                  </button>
                </div>
              ) : (
                <form onSubmit={submitIntent} className="mt-6 space-y-5">
                  {/* Amounts */}
                  <div>
                    <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-npbc-gold">Amount</div>
                    <div className="mt-3 grid grid-cols-5 gap-2">
                      {quickAmounts.map((a) => (
                        <button
                          type="button"
                          key={a}
                          onClick={() => { setAmount(a); setCustomAmount(""); }}
                          data-testid={`give-amount-${a}`}
                          className={`rounded-xl border py-3 text-sm font-semibold transition ${
                            amount === a && !customAmount
                              ? "border-npbc-gold bg-npbc-gold text-npbc-black"
                              : "border-white/15 bg-white/[0.04] text-white hover:border-npbc-gold/60"
                          }`}
                        >
                          ${a}
                        </button>
                      ))}
                    </div>
                    <div className="mt-3 relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 text-sm">$</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        placeholder="Other amount"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        data-testid="give-amount-custom"
                        className="w-full rounded-xl border border-white/15 bg-white/[0.04] pl-8 pr-4 py-3 text-sm outline-none placeholder:text-white/45 focus:border-npbc-gold"
                      />
                    </div>
                  </div>

                  {/* Fund */}
                  <div>
                    <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-npbc-gold">Designation</div>
                    <select
                      value={fund}
                      onChange={(e) => setFund(e.target.value)}
                      data-testid="give-fund"
                      className="mt-3 w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm outline-none focus:border-npbc-gold"
                    >
                      {funds.map((f) => (
                        <option key={f.id} value={f.label} className="text-npbc-black">
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Frequency */}
                  <div>
                    <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-npbc-gold">Frequency</div>
                    <div className="mt-3 grid grid-cols-4 gap-2">
                      {frequencies.map((f) => (
                        <button
                          key={f}
                          type="button"
                          onClick={() => setFrequency(f)}
                          data-testid={`give-frequency-${f.toLowerCase().replace(/\s|-/g, "")}`}
                          className={`rounded-xl border py-2.5 text-xs font-semibold transition ${
                            frequency === f
                              ? "border-npbc-gold bg-npbc-gold text-npbc-black"
                              : "border-white/15 bg-white/[0.04] text-white hover:border-npbc-gold/60"
                          }`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                    {frequency !== "One-time" && (
                      <p className="mt-2 text-[11px] text-white/55">
                        Recurring Cash App gifts are set up inside the Cash App itself after your first gift.
                      </p>
                    )}
                  </div>

                  {/* Contact */}
                  <div className="grid gap-2">
                    <input
                      required
                      placeholder="Your name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="give-name"
                      className="rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm outline-none placeholder:text-white/45 focus:border-npbc-gold"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="email"
                        placeholder="Email (optional)"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="give-email"
                        className="rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm outline-none placeholder:text-white/45 focus:border-npbc-gold"
                      />
                      <input
                        placeholder="Phone (optional)"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        data-testid="give-phone"
                        className="rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm outline-none placeholder:text-white/45 focus:border-npbc-gold"
                      />
                    </div>
                    <textarea
                      rows={2}
                      placeholder="Notes to the church (optional)"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      data-testid="give-notes"
                      className="rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm outline-none placeholder:text-white/45 focus:border-npbc-gold"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="give-submit"
                    className="btn-gold w-full justify-center !py-4"
                  >
                    {busy ? "Recording your gift..." : `Give $${effectiveAmount || 0} via Cash App`}
                    <ExternalLink className="h-4 w-4 opacity-70" />
                  </button>
                  <p className="text-[11px] text-white/55 text-center">
                    You'll be redirected to Cash App to complete your secure gift.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Ways to give */}
      <section id="ways" className="bg-npbc-cream py-24">
        <div className="container-npbc">
          <div data-reveal>
            <SectionHeader eyebrow="Ways to Give" title="Give in the way that works best for you." />
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Smartphone, title: "Cash App", body: `Send your gift instantly via Cash App to ${church.giving.cashapp}.`, cta: { label: "Give via Cash App", href: church.giving.cashappUrl, external: true } },
              { icon: DollarSign, title: "In Person", body: "Give during any Sunday worship service using the offering plate.", cta: null },
              { icon: Mail,       title: "By Mail",   body: 'Mail your check made payable to "New Philadelphia Missionary Baptist Church" to our office address.', cta: null },
              { icon: Repeat,     title: "Recurring", body: "Set up a recurring gift and let generosity become a rhythm of your life.", cta: null },
            ].map((w, i) => (
              <div key={w.title} data-reveal style={{ transitionDelay: `${i * 60}ms` }} className="card-elevated p-8 h-full flex flex-col">
                <div className="h-12 w-12 rounded-2xl bg-npbc-red/10 text-npbc-red grid place-items-center">
                  <w.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 heading-serif text-2xl">{w.title}</h3>
                <p className="mt-2 text-sm text-npbc-gray leading-relaxed flex-1">{w.body}</p>
                {w.cta && (
                  <a href={w.cta.href} target={w.cta.external ? "_blank" : undefined} rel="noreferrer noopener" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-npbc-red">
                    {w.cta.label} <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where your gift goes */}
      <section className="bg-white py-24">
        <div className="container-npbc">
          <div data-reveal>
            <SectionHeader
              align="center"
              eyebrow="Where Your Gift Goes"
              title="Kingdom impact, one gift at a time."
              subtitle="Every dollar is deployed with prayer and accountability across three focus areas."
            />
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {funds.slice(0, 3).map((f, i) => (
              <div key={f.id} data-reveal style={{ transitionDelay: `${i * 60}ms` }} className="card-elevated p-8">
                <div className="h-12 w-12 rounded-2xl bg-npbc-gold/25 text-npbc-black grid place-items-center">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 heading-serif text-2xl">{f.label}</h3>
                <p className="mt-2 text-npbc-gray leading-relaxed text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-npbc-black text-white py-16">
        <div className="container-npbc text-center max-w-2xl mx-auto" data-reveal>
          <div className="eyebrow !text-npbc-gold justify-center flex">Thank You</div>
          <h3 className="mt-3 heading-serif text-4xl">You are a partner in the gospel.</h3>
          <p className="mt-3 text-white/70">
            Your generosity keeps the lights on, the doors open, and the good news going out. On behalf of Pastor Little and the entire family of New Philadelphia — thank you.
          </p>
          <a href="#give-now" className="btn-gold mt-8">
            <Heart className="h-4 w-4" /> Give Now
          </a>
        </div>
      </section>
    </div>
  );
}
